import React from "react";
import Box from "@mui/material/Box";
import { CircularProgress, Button } from "@mui/material";
import { useActiveVehicle } from "../../../context/ActiveVehicleProvider";
import { useTicketTypeMeta } from "../../../context/TicketTerminologyProvider";
import { getNextViolationTicketType } from "../../../utils/getNextViolationTicketType";
import ViolationRecordCard from "./cards/ViolationRecordCard";
import QuickSummaryCard from "./cards/QuickSummaryCard";
import VehicleRecordCard from "./cards/VehicleRecordCard";
import ScannedPlateHeader from "../../scannedPlateHeader/ScannedPlateHeader";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface ActiveVehicleDrawerProps {}

const ActiveVehicleDrawerActionStage: React.FC<
  ActiveVehicleDrawerProps
> = ({}) => {
  const {
    isLoading,
    closeDrawer,
    currentSearch,
    inWindowTickets,
    parkingLocation,
    parkingAuthorization,
    ticketFormMethods,
    goNext,
  } = useActiveVehicle();
  const getTicketTypeMeta = useTicketTypeMeta();

  // Mirror of the primary CTA in ViolationActions (rendered mid-card) so the
  // proceed action is also reachable at the bottom-right, matching every other
  // stage's "next" placement. Same color/action, but the shorter chip label.
  const decision = parkingLocation
    ? getNextViolationTicketType({
        tickets: inWindowTickets,
        parkingLocation,
      })
    : null;
  const suppressPrimaryCta =
    parkingAuthorization?.allowed ||
    decision?.action === "withinLimit" ||
    decision?.action === "sessionClosed";
  const primaryMeta =
    decision?.action === "suggestTicket"
      ? getTicketTypeMeta(decision.ticketType)
      : null;
  const showPrimaryCta = !isLoading && !suppressPrimaryCta && !!primaryMeta;

  const handlePrimary = () => {
    if (decision?.action !== "suggestTicket") return;
    ticketFormMethods.setValue("ticketType", decision.ticketType);
    goNext();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          px: 2,
          pb: 2,
          maxWidth: 600,
          width: "100%",
          flexDirection: "column",
        }}
      >
        <Box display="flex" gap={2}>
          <ScannedPlateHeader
            variant="inline"
            licenseNumber={currentSearch?.licenseNumber ?? ""}
            licenseState={currentSearch?.licenseState ?? ""}
          />
          <VehicleRecordCard />
        </Box>
        {/* Stable wrapper so the tour's action anchor exists the moment the
            drawer opens — the decision cards below mount late (after the
            vehicle-history fetch), which would otherwise leave the tour popover
            hidden until they load. */}
        <Box
          data-tour="ticket-action"
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          {isLoading ? (
            <Box
              display="flex"
              width="100%"
              justifyContent="center"
              alignItems="center"
              sx={{ py: 4 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <QuickSummaryCard />
              <ViolationRecordCard />
            </>
          )}
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={closeDrawer}
            startIcon={<ArrowBackIosNewIcon fontSize="small" />}
          >
            Back
          </Button>
          {showPrimaryCta && (
            <Button
              variant="contained"
              color={primaryMeta!.buttonColor}
              onClick={handlePrimary}
              endIcon={<ArrowForwardIosIcon fontSize="small" />}
            >
              {primaryMeta!.shortLabel}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ActiveVehicleDrawerActionStage;
